import React from 'react';
import { useAtomValue } from 'jotai';
import type { SearchSuggestion } from '@rocco/ui/search/view-models/search-suggestion';
import type { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { SearchSuggestedCategories } from './SearchSuggestedCategories';
import { SearchResultPageTilesGrid } from './SearchResultPageTilesGrid';
import { SearchPageRecentlyViewed } from './SearchPageRecentlyViewed';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import type { useFetcher } from '@remix-run/react';

interface SearchPageContentSectionsDesktopProps {
    filterSortProps: ReturnType<typeof useCategoryFilterSort>;
    onSuggestedCategoryClick: (category: SearchSuggestion) => void;
    fetcherState: ReturnType<typeof useFetcher>['state'];
    isLoading: boolean;
}

/**
 * Renders the content sections for the desktop view of the search page.
 * Includes suggested categories, search results grid, and recently viewed items.
 */
export const SearchPageContentSectionsDesktop: React.FC<
    SearchPageContentSectionsDesktopProps
> = ({
    filterSortProps,
    onSuggestedCategoryClick,
    fetcherState,
    isLoading,
}) => {
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const suggestedCategories = pageDataFromAtom?.suggestedCategories ?? [];
    const recentlyViewedItems = pageDataFromAtom?.recentlyViewedItems ?? [];

    return (
        <div className="space-y-35">
            {suggestedCategories.length > 0 ? (
                <SearchSuggestedCategories
                    onSuggestedCategoryClick={onSuggestedCategoryClick}
                />
            ) : null}
            <section>
                <SearchResultPageTilesGrid
                    {...filterSortProps}
                    fetcherState={fetcherState}
                    isLoading={isLoading}
                />
            </section>
            {recentlyViewedItems.length > 0 ? (
                <section className="pb-28">
                    <SearchPageRecentlyViewed />
                </section>
            ) : null}
        </div>
    );
};
